import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Activity,
    HardDrive,
    Inbox,
    Clock,
    Users,
    FileText,
    RefreshCw,
    Loader2,
    AlertTriangle,
    ShieldCheck,
} from "lucide-react";
import { CATEGORY_LABELS, formatBytes } from "../lib/documents";
import { AuditAssignmentsDialog } from "../components/AuditAssignmentsDialog";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const fmtSize = (bytes) => formatBytes(bytes || 0);

export default function ImportStatusPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);

    const fetchStats = async () => {
        try {
            const { data } = await api.get("/migration/import-stats");
            setStats(data);
        } catch (e) {
            console.error("Errore caricamento stats", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(fetchStats, 5000);
        return () => clearInterval(id);
    }, [autoRefresh]);

    const totalGb = stats ? (stats.total_size_bytes / (1024 ** 3)).toFixed(2) : "0";
    const maxCatCount = stats?.by_category?.length ? Math.max(...stats.by_category.map(c => c.count)) : 1;
    const maxYearCount = stats?.by_year?.length ? Math.max(...stats.by_year.map(y => y.count)) : 1;

    const topEmployees = stats?.top_employees || [];
    const { sortedItems: topEmpSorted, sortKey: empKey, sortDir: empDir, requestSort: empSort } = useSortableTable(topEmployees, {
        customAccessors: {
            dipendente: (e) => `${e.cognome || ""} ${e.nome || ""}`.toLowerCase(),
        },
    });

    const recentUploads = stats?.recent_uploads || [];
    const { sortedItems: recentSorted, sortKey: upKey, sortDir: upDir, requestSort: upSort } = useSortableTable(recentUploads, {
        customAccessors: {
            file: (d) => (d.filename || "").toLowerCase(),
        },
    });

    return (
        <div data-testid="import-status-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Activity className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Stato migrazione documenti</h1>
                            <p className="text-sm text-slate-600">
                                Statistiche dettagliate per monitorare l'import massivo dei documenti (~10&nbsp;GB).
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setAuditOpen(true)}
                            className="gap-2 border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                            data-testid="open-audit-btn"
                        >
                            <ShieldCheck className="h-4 w-4" />
                            Verifica assegnazioni
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setAutoRefresh(v => !v)}
                            className={`gap-2 ${autoRefresh ? "border-emerald-300 bg-emerald-50 text-emerald-700" : ""}`}
                            data-testid="auto-refresh-toggle"
                        >
                            <RefreshCw className={`h-4 w-4 ${autoRefresh ? "animate-spin" : ""}`} />
                            {autoRefresh ? "Auto (5s)" : "Auto-refresh"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={fetchStats}
                            disabled={loading}
                            className="gap-2"
                            data-testid="refresh-btn"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Aggiorna
                        </Button>
                    </div>
                </div>

                <div className={`flex items-center justify-center py-16 text-slate-500 ${loading && !stats ? "" : "hidden"}`}>
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>

                <div className={stats ? "" : "hidden"}>
                    {/* Top cards */}
                    <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                        <StatCard
                            label="Documenti totali"
                            value={stats?.total_documents?.toLocaleString("it-IT") || "0"}
                            icon={<FileText className="h-4 w-4" />}
                            testId="stat-total-docs"
                        />
                        <StatCard
                            label="Spazio occupato"
                            value={`${totalGb} GB`}
                            sub={fmtSize(stats?.total_size_bytes)}
                            icon={<HardDrive className="h-4 w-4" />}
                            tone={parseFloat(totalGb) > 8 ? "warn" : "default"}
                            testId="stat-size"
                        />
                        <StatCard
                            label="In attesa"
                            value={stats?.pending_documents?.toLocaleString("it-IT") || "0"}
                            icon={<Inbox className="h-4 w-4" />}
                            tone={(stats?.pending_documents || 0) > 0 ? "warn" : "ok"}
                            link={(stats?.pending_documents || 0) > 0 ? "/documenti/in-attesa" : null}
                            testId="stat-pending"
                        />
                        <StatCard
                            label="Ultime 24h"
                            value={stats?.uploads_last_24h?.toLocaleString("it-IT") || "0"}
                            sub={`${stats?.uploads_last_1h || 0} ult. ora`}
                            icon={<Clock className="h-4 w-4" />}
                            tone="ok"
                            testId="stat-24h"
                        />
                        <StatCard
                            label="Dipendenti coperti"
                            value={`${stats?.employees_with_docs || 0} / ${stats?.total_employees || 0}`}
                            sub={`${stats?.employees_without_docs || 0} senza documenti`}
                            icon={<Users className="h-4 w-4" />}
                            tone={(stats?.employees_without_docs || 0) > 50 ? "warn" : "default"}
                            testId="stat-coverage"
                        />
                    </div>

                    {/* Two columns: by category + by year */}
                    <div className="mb-6 grid gap-6 lg:grid-cols-2">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-500">Per categoria</h2>
                            <div className="space-y-2">
                                {(stats?.by_category || []).map((c) => (
                                    <BarRow
                                        key={c.category}
                                        label={CATEGORY_LABELS[c.category] || c.category}
                                        count={c.count}
                                        sub={fmtSize(c.size_bytes)}
                                        ratio={c.count / maxCatCount}
                                    />
                                ))}
                                <div className={`py-8 text-center text-sm text-slate-400 ${(stats?.by_category?.length || 0) === 0 ? "" : "hidden"}`}>
                                    Nessun documento caricato.
                                </div>
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-500">Per anno</h2>
                            <div className="space-y-2">
                                {(stats?.by_year || []).map((y) => (
                                    <BarRow
                                        key={y.year}
                                        label={y.year}
                                        count={y.count}
                                        ratio={y.count / maxYearCount}
                                    />
                                ))}
                                <div className={`py-8 text-center text-sm text-slate-400 ${(stats?.by_year?.length || 0) === 0 ? "" : "hidden"}`}>
                                    Nessun documento.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Two columns: top employees + recent uploads */}
                    <div className="grid gap-6 lg:grid-cols-2">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-500">Top 10 dipendenti per # documenti</h2>
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <SortableHead sortKey="dipendente" sortKeyCurrent={empKey} sortDir={empDir} onClick={empSort}>
                                            Dipendente
                                        </SortableHead>
                                        <SortableHead sortKey="count" sortKeyCurrent={empKey} sortDir={empDir} onClick={empSort} className="text-right">
                                            Documenti
                                        </SortableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {topEmpSorted.map((emp) => (
                                        <TableRow key={emp.id}>
                                            <TableCell>
                                                <Link to={`/dipendenti/${emp.id}`} className="hover:text-[#376DB3]">
                                                    <span className="font-medium">{emp.cognome} {emp.nome}</span>
                                                    <span className="ml-2 text-xs text-slate-500">matr. {emp.matricola}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell className="text-right font-mono-numbers font-semibold text-[#376DB3]">
                                                {emp.count}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(stats?.top_employees?.length || 0) === 0 && (
                                        <TableRow><TableCell colSpan={2} className="py-8 text-center text-sm text-slate-400">Nessun dato.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-500">Ultimi 20 upload</h2>
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <SortableHead sortKey="file" sortKeyCurrent={upKey} sortDir={upDir} onClick={upSort}>
                                            File
                                        </SortableHead>
                                        <SortableHead sortKey="uploaded_at" sortKeyCurrent={upKey} sortDir={upDir} onClick={upSort}>
                                            Quando
                                        </SortableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {recentSorted.map((d) => (
                                        <TableRow key={d.id}>
                                            <TableCell className="max-w-xs">
                                                <div className="truncate text-sm font-medium" title={d.filename}>{d.filename}</div>
                                                <div className="text-xs text-slate-500">
                                                    {CATEGORY_LABELS[d.category] || d.category}
                                                    {" · "}{fmtSize(d.size_bytes)}
                                                </div>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-xs text-slate-600 font-mono-numbers">
                                                {fmtDateTime(d.uploaded_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(stats?.recent_uploads?.length || 0) === 0 && (
                                        <TableRow><TableCell colSpan={2} className="py-8 text-center text-sm text-slate-400">Nessun upload recente.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    {/* Anomaly hint */}
                    <div className={`mt-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 ${(stats?.pending_documents || 0) > 0 ? "" : "hidden"}`}>
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <div>
                            Ci sono <b>{stats?.pending_documents}</b> documenti in attesa di assegnazione manuale.{" "}
                            <Link to="/documenti/in-attesa" className="font-semibold underline">Vai alla pagina di assegnazione →</Link>
                        </div>
                    </div>
                </div>
            </main>
            <AuditAssignmentsDialog open={auditOpen} onOpenChange={setAuditOpen} />
        </div>
    );
}

const StatCard = ({ label, value, sub, icon, tone, link, testId }) => {
    const colorByTone = {
        warn: "text-amber-600",
        ok: "text-emerald-600",
    };
    const valueColor = colorByTone[tone] || "text-[#376DB3]";
    const iconBg = tone === "warn"
        ? "bg-amber-50 text-amber-600"
        : tone === "ok"
        ? "bg-emerald-50 text-emerald-600"
        : "bg-slate-100 text-[#376DB3]";
    const card = (
        <div className="flex h-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md" data-testid={testId}>
            <div className="min-w-0">
                <div className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
                <div className={`font-display text-2xl font-bold font-mono-numbers ${valueColor}`}>{value}</div>
                <div className={`text-[11px] text-slate-500 ${sub ? "" : "hidden"}`}>{sub || ""}</div>
            </div>
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md ${iconBg}`}>{icon}</div>
        </div>
    );
    if (link) return <Link to={link}>{card}</Link>;
    return card;
};

const BarRow = ({ label, count, sub, ratio }) => (
    <div className="flex items-center gap-3">
        <div className="w-32 flex-shrink-0 text-sm font-medium text-slate-700">{label}</div>
        <div className="relative h-6 flex-1 overflow-hidden rounded bg-slate-100">
            <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#376DB3] to-[#5BAEDB] transition-all"
                style={{ width: `${Math.max(2, Math.round(ratio * 100))}%` }}
            />
        </div>
        <div className="w-24 flex-shrink-0 text-right">
            <div className="font-mono-numbers text-sm font-semibold text-[#376DB3]">{count.toLocaleString("it-IT")}</div>
            <div className={`text-[11px] text-slate-500 ${sub ? "" : "hidden"}`}>{sub || ""}</div>
        </div>
    </div>
);
